import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { InfoMessage, InfoMessageTypeEnum } from '@websky/graphql';
import { CdnIcon } from '@shared/ui';

import Notification from '../../../../../BaseComponents/Notification';

import { useTheme } from '../../../../../theme';

export interface Props {
	showCaculationMessage?: boolean;
	showReturnMessage?: boolean;
	showRefundSeatsMessage?: boolean;
	showNasService?: boolean;
	showVskMessage?: boolean;
	showInsuranceAlphaOrSirenaMessage?: boolean;
}

const RefundNotifications: React.FC<Props> = ({
	showCaculationMessage,
	showReturnMessage,
	showRefundSeatsMessage,
	showNasService,
	showVskMessage,
	showInsuranceAlphaOrSirenaMessage
}) => {
	const { RefundNotifications: css } = useTheme('Entities').Refund;

	const { t } = useTranslation('Refund');

	const notes = useMemo(() => {
		const notes: InfoMessage[] = [];

		if (showReturnMessage) {
			notes.push({
				title: t('Note'),
				text: t('The money will be returned to the card used to pay for the ticket within 15 days'),
				type: InfoMessageTypeEnum.Info,
				code: 'isAutoMode'
			});
		}

		return notes;
	}, [showReturnMessage]);

	const warnings = useMemo(() => {
		const warnings: InfoMessage[] = [];

		if (showNasService) {
			warnings.push({
				title: t('You should return airport services for all passengers to refund or exchange air tickets'),
				text: t(
					'Return of services is made automatically when you click on the «Return services» button. The money will be credited to the card within 10 working days'
				),
				type: InfoMessageTypeEnum.Info,
				code: 'withNasServices'
			});
		}

		if (showCaculationMessage) {
			warnings.push({
				title: t('Note'),
				text: t(
					'The possibility and amount of refund will be calculated by the operator after sending the application. The answer will be sent to your E-mail and is also available on the order page.'
				),
				type: InfoMessageTypeEnum.Info,
				code: 'notIsAutoMode'
			});
		}

		if (showVskMessage) {
			warnings.push({
				title: t('Note'),
				text: t('When the refund is performed, VSK insurance policies will be canceled'),
				type: InfoMessageTypeEnum.Info,
				code: 'notIsAutoMode'
			});
		}

		if (showInsuranceAlphaOrSirenaMessage) {
			warnings.push({
				title: t('Note'),
				text: t('Refund_insurance_alpha_sirena_message'),
				type: InfoMessageTypeEnum.Info,
				code: 'withInsuranceAlphaOrSirena'
			});
		}

		return warnings;
	}, [showNasService, showVskMessage, showCaculationMessage, showInsuranceAlphaOrSirenaMessage]);

	const errors = useMemo(() => {
		const errors: InfoMessage[] = [];

		if (showRefundSeatsMessage) {
			errors.push({
				title: t('Refund restrictions'),
				text: t('After submitting the request, the seats you have selected will be canceled.'),
				type: InfoMessageTypeEnum.Warning,
				code: 'notIsAutoMode'
			});
		}

		return errors;
	}, [showRefundSeatsMessage]);

	const notificationsStyleProps = {
		className: css.notification,
		globalClassName: css.notification__global,
		titleClassName: css.notification__title,
		textClassName: css.notification__text
	};

	return (
		<div className={css.wrapper}>
			{notes.map((note, index) => (
				<Notification
					{...notificationsStyleProps}
					key={`${note.code}-${index}`}
					status="note"
					type="global"
					title={note.title}
					text={note.text}
					code={note.code}
				/>
			))}

			{warnings.map((warning, index) => (
				<Notification
					{...notificationsStyleProps}
					key={`${warning.code}-${index}`}
					status="warning"
					type="global"
					title={warning.title}
					text={warning.text}
					code={warning.code}
				/>
			))}

			{errors.map((error, index) => (
				<Notification
					{...notificationsStyleProps}
					key={`${error.code}-${index}`}
					status="error"
					icon={<CdnIcon icon="Info/Not included" />}
					type="global"
					title={error.title}
					text={error.text}
					code={error.code}
				/>
			))}
		</div>
	);
};

export default RefundNotifications;
